import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Window } from '@progress/kendo-react-dialogs';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import { KendoLocalizationProvider } from '@contexts/kendo-localization-context';
import I18n from '@utils/i18n';
import api from '@utils/axios';
import DOMPurify from 'dompurify';
import { produce } from 'immer';
import kendoDatatableParams from '../../helpers/kendoDatatableParams';
import {
  customersQuickBooksSessionPath,
  importQbCustomersAccountsPath,
  importQbCustomersBuildingsPath
} from '../../routes';
import SearchPanel from '../../components/SearchPanel';
import { INITIAL_PAGE_SKIP } from '../InspectionsIndex/constants';
import { getInitialData } from './helpers/initialData';
import { ALL_COLUMNS, ALL_COLUMNS_DATA, IMPORT_TO, STATUS } from './constants';
import MassActions from './nested/MassActions';
import './index.css';

function ImportFromQB({ type, cacheKey, importTo }) {
  const initialData = useMemo(() => getInitialData(cacheKey), []);
  const [page, setPage] = useState(initialData.page);
  const [sort, setSort] = useState(initialData.sort);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(STATUS.LOADING);
  const [selectAll, setSelectAll] = useState(false);
  const [customers, setCustomers] = useState({ customers: [], total_count: 0 });
  const selectedCustomers = customers.customers.filter((x) => x.selected);
  const [response, setResponse] = useState(null);

  const allColumnsWrapped = useMemo(() => ALL_COLUMNS.map((column) => ALL_COLUMNS_DATA[column]), []);

  useEffect(() => {
    setStatus(STATUS.LOADING);
    api
      .get(customersQuickBooksSessionPath(), {
        params: kendoDatatableParams({
          page,
          sort,
          search,
          // Accounts import: ask the server for top-level customers only, so QBO
          // sub-customers/Projects are never offered as standalone Accounts (IP-15521).
          optionalParams: { top_level_only: importTo === IMPORT_TO.ACCOUNT }
        })
      })
      .then(({ data }) => setCustomers(data))
      .finally(() => {
        setStatus(STATUS.READY);
      });
  }, [page]);

  const headerSelectionValue =
    selectAll || (customers.total_count > 0 && customers.customers.every((customer) => customer.selected));

  const onSearchChange = (value) => {
    setSearch(value);
    setPage((prevState) => ({ ...prevState, skip: INITIAL_PAGE_SKIP }));
  };

  const onSelectionChange = useCallback((event) => {
    setSelectAll(false);
    setCustomers(
      produce((draft) => {
        const customer = draft.customers.find((c) => c.id === event.dataItem.id);
        customer.selected = !customer.selected;
      })
    );
  }, []);

  const onSelectAll = useCallback(() => {
    setSelectAll(true);
    setCustomers(
      produce((draft) => {
        draft.customers.forEach((customer) => {
          customer.selected = true;
        });
      })
    );
  }, []);

  const onHeaderSelectionChange = useCallback((event) => {
    if (!event.syntheticEvent.target.checked) {
      setSelectAll(false);
    }
    setCustomers(
      produce((draft) => {
        draft.customers.forEach((customer) => {
          customer.selected = event.syntheticEvent.target.checked;
        });
      })
    );
  }, []);

  const loadData = useCallback((pageData) => {
    setSelectAll(false);
    if (pageData) return setPage(pageData);

    setPage((prevState) => ({ ...prevState, skip: INITIAL_PAGE_SKIP }));
  }, []);

  const handleImportClick = () => {
    setStatus(STATUS.IMPORTING);

    const params = selectAll ? { type, all: true } : { type, qb_ids: selectedCustomers.map((x) => x.id) };

    api
      .post(
        importTo === IMPORT_TO.BUILDING ? importQbCustomersBuildingsPath() : importQbCustomersAccountsPath(),
        params,
        {
          timeout: 0
        }
      )
      .then((res) => {
        const selectedIds = selectedCustomers.map((x) => x.id);
        setCustomers(
          produce((draft) => {
            draft.customers.forEach((customer) => {
              if (selectedIds.includes(customer.id)) {
                customer.imported = true;
                customer.selected = false;
              }
            });
          })
        );
        setResponse(res.data);
        setStatus(STATUS.COMPLETE);
      })
      .catch(() => {
        setStatus(STATUS.ERROR);
      });
  };

  return (
    <KendoLocalizationProvider>
      <MassActions
        customers={customers}
        selectAll={selectAll}
        onSelectAll={onSelectAll}
        onImport={handleImportClick}
        importing={status === STATUS.IMPORTING}
        type={type}
        importTo={importTo}
        loading={status === STATUS.LOADING}>
        <div className="content-actions__group--search" style={{ marginLeft: 'auto', flex: '0 0 auto' }}>
          <SearchPanel placeholder={I18n.t('filters.search')} onChange={onSearchChange} />
        </div>
      </MassActions>

      <hr className="workspace__split--sticky" />
      {status === STATUS.LOADING && (
        <div className="workspace__section">
          <div className="qmb-loading--96" data-title="Loading">
            <svg role="img">
              <use href="/map.svg#load-spinner" />
            </svg>
          </div>
        </div>
      )}
      {status !== STATUS.LOADING && (
        <Grid
          className="qmb-grid--01"
          data={customers.customers}
          total={customers.total_count}
          skip={page.skip}
          take={page.take}
          onPageChange={(event) => {
            loadData(event.page);
          }}
          selectedField="selected"
          pageable={{
            type: 'input',
            pageSizes: [25, 50, 100],
            pageSizeValue: page.take
          }}
          pager={EnterKeyPager}
          sortable
          sort={sort}
          onSortChange={(event) => {
            setSort(event.sort);
            loadData();
          }}
          onSelectionChange={onSelectionChange}
          onHeaderSelectionChange={onHeaderSelectionChange}>
          {allColumnsWrapped
            .filter((x) => !x.hidden)
            .map((column) => (
              <GridColumn
                key={column.id}
                field={column.field}
                title={column.title}
                cell={column.cell}
                headerSelectionValue={headerSelectionValue}
                sortable={column.sortable}
                width={column.width}
                className={column.className}
                headerClassName={column.headerClassName}
              />
            ))}
        </Grid>
      )}

      {(status === STATUS.IMPORTING || status === STATUS.COMPLETE || status === STATUS.ERROR) && (
        <Window
          modal
          doubleClickStageChange={false}
          draggable={false}
          maximizeButton={() => null}
          minimizeButton={() => null}
          onClose={() => setStatus(STATUS.READY)}
          initialHeight={0}>
          {status === STATUS.IMPORTING && <Spinner />}
          {status === STATUS.COMPLETE && <Complete html={response} />}
          {status === STATUS.ERROR && <Error />}
        </Window>
      )}
    </KendoLocalizationProvider>
  );
}

function Spinner() {
  return (
    <div className="qmb-loading--96" data-title="Importing...">
      <svg role="img">
        <use href="/map.svg#load-spinner" />
      </svg>
    </div>
  );
}

function Complete({ html }) {
  const sanitizedHtml = useMemo(() => DOMPurify.sanitize(html || ''), [html]);

  // The import endpoint returns formatted HTML; sanitize it before rendering.
  // eslint-disable-next-line react/no-danger
  return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
}

function Error() {
  return <>An error occurred. Please try again.</>;
}

ImportFromQB.propTypes = {
  type: PropTypes.string.isRequired,
  cacheKey: PropTypes.string.isRequired,
  importTo: PropTypes.string.isRequired
};

Complete.propTypes = {
  html: PropTypes.string
};

Complete.defaultProps = {
  html: null
};

export default ImportFromQB;
